import { useMemo } from 'react';
import { Box, Text, Stack, Paper, Group, SimpleGrid } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { InlineIcon } from '@iconify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { searchUsers } from 'api/users';
import { useMe } from 'store/userStore';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing.xl,
  },
  icon: {
    color: theme.colors.teal[5],
    marginBottom: theme.spacing.md,
  },
  card: {
    textAlign: 'center',
    padding: theme.spacing.md,
  },
  cardValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1,
  },
}));

export const Landing = () => {
  const { classes } = useStyles();
  const me = useMe();
  const { data } = useInfiniteQuery(
    ['ai-coach-assistant-users', me?.userId, ''],
    ({ pageParam = 1 }) =>
      searchUsers({
        query: { coachId: me?.userId },
        limit: 30,
        page: pageParam,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.items) return undefined;
        const nextPage = allPages.length + 1;
        return lastPage.items.length === 30 ? nextPage : undefined;
      },
      enabled: !!me?.userId,
      staleTime: 1000 * 30,
    }
  );

  const users = useMemo(
    () => data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    [data?.pages]
  );

  const stats = useMemo(() => {
    if (!users) return { disengaged: 0, followup: 0, meetingToday: 0 };
    return {
      disengaged: users.filter((u) => u.followupBucket?.isDisengaged).length,
      followup: users.filter((u) => u.followupBucket?.isFollowupRequired)
        .length,
      meetingToday: users.filter((u) => u.followupBucket?.isMeetingCoachToday)
        .length,
    };
  }, [users]);

  return (
    <Box className={classes.container}>
      <Stack align="center" gap="sm">
        <InlineIcon
          icon="material-symbols:auto-awesome"
          height={48}
          className={classes.icon}
        />
        <Text size="xl" fw={600}>
          AI Coach Assistant
        </Text>
        <Text size="sm" c="dimmed" maw={400} ta="center">
          Select a user from the list to start a conversation. I can help you
          check engagement, prepare for meetings, and draft messages.
        </Text>

        {users && users.length > 0 && (
          <SimpleGrid cols={3} spacing="md" mt="xl">
            <Paper withBorder className={classes.card}>
              <Group justify="center" gap="xs" mb={4}>
                <InlineIcon
                  icon="tabler:alert-triangle"
                  height={16}
                  color="red"
                />
                <Text className={classes.cardValue} c="red">
                  {stats.disengaged}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Disengaged
              </Text>
            </Paper>
            <Paper withBorder className={classes.card}>
              <Group justify="center" gap="xs" mb={4}>
                <InlineIcon
                  icon="tabler:message-dots"
                  height={16}
                  color="orange"
                />
                <Text className={classes.cardValue} c="orange">
                  {stats.followup}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Need follow-up
              </Text>
            </Paper>
            <Paper withBorder className={classes.card}>
              <Group justify="center" gap="xs" mb={4}>
                <InlineIcon
                  icon="tabler:calendar-event"
                  height={16}
                  color="green"
                />
                <Text className={classes.cardValue} c="green">
                  {stats.meetingToday}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Meetings today
              </Text>
            </Paper>
          </SimpleGrid>
        )}
      </Stack>
    </Box>
  );
};
